import React, { useRef, useEffect, useState } from "react";
import { Skeleton } from "antd";
import parse from "html-react-parser";
import PopoverToolbar from "../EbulletinPopoverToolbar";

// Save and restore selection for text editing
let savedSelection = null;

// Function to save the current selection
function saveSelection() {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    savedSelection = sel.getRangeAt(0);
  }
}

// Function to restore the saved selection
function restoreSelection() {
  const sel = window.getSelection();
  if (savedSelection) {
    sel.removeAllRanges();
    sel.addRange(savedSelection);
  }
}

export default function TextBlock({
  block,
  isEditing,
  updateBlockContent,
  updateBlockStyle,
}) {
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});

  const [currentFontSize, setCurrentFontSize] = useState(
    block.style?.fontSize || "body"
  );
  const [currentFontColor, setCurrentFontColor] = useState(
    block.style?.color || "#000000"
  );

  // Update active formats based on current selection
  const updateActiveFormats = () => {
    if (!editorRef.current || !window.getSelection().anchorNode) return;
    let node = window.getSelection().anchorNode;
    let isLink = false;
    while (node && node !== editorRef.current) {
      if (node.nodeName === "A") {
        isLink = true;
        break;
      }
      node = node.parentNode;
    }
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      isLink,
    });
  };

  // Update block content when editor changes
  const updateBlockParagraph = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    updateBlockContent(html);
    const text = editorRef.current.innerText.replace(/\u200B/g, "").trim();
    const isEmpty = text === "" || html === "<br>" || html === "";
    editorRef.current.classList.toggle("is-empty", isEmpty);
  };

  const handleSelection = () => {
    const selection = window.getSelection();
    if (
      !selection ||
      selection.isCollapsed ||
      !editorRef.current?.contains(selection.anchorNode)
    ) {
      setShowToolbar(false);
      return;
    }
    setShowToolbar(true);
    saveSelection(); // Save the selection when user selects text
    updateActiveFormats();
  };

  // Handle text formatting commands
  const execCommand = (command, value = null) => {
    editorRef.current.focus();
    restoreSelection(); // Restore selection so commands apply to highlighted text
    document.execCommand("styleWithCSS", false, true); // Enables inline styles (like color)
    document.execCommand(command, false, value);
    updateBlockParagraph();
    updateActiveFormats();
  };

  // Handle alignment changes
  const handleAlignmentChange = (align) => {
    const alignmentCommands = {
      left: "justifyLeft",
      center: "justifyCenter",
      right: "justifyRight",
    };
    execCommand(alignmentCommands[align]);
    updateBlockStyle({ ...block.style, textAlign: align });
  };

  // Handle font size changes
  const handleFontSizeChange = (size) => {
    const sizeMap = {
      heading: "38px",
      subheading: "24px",
      body: "16px",
    };
    const pixelSize = sizeMap[size];

    editorRef.current.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      // Use insertHTML to wrap the selection in a span with the correct font size.
      const html = `<span style="font-size: ${pixelSize};">${selection.toString()}</span>`;
      document.execCommand("insertHTML", false, html);
    }

    updateBlockParagraph();
    setCurrentFontSize(size);
  };

  // Handle font color changes
  const handleFontColorChange = (color) => {
    execCommand("foreColor", color);
    setCurrentFontColor(color);
    updateBlockStyle({ ...block.style, color });
  };

  // Handle link creation
  const handleLinkClick = () => {
    const url = prompt("Enter the URL:", "https://");
    if (url) {
      execCommand("createLink", url);
    }
  };

  // Handle link removal
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !toolbarRef.current?.contains(e.target) &&
        !editorRef.current?.contains(e.target)
      ) {
        setShowToolbar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!editorRef.current || !isEditing) return;
    const el = editorRef.current;
    if (el.innerHTML.trim() !== (block.content || "").trim()) {
      el.innerHTML = block.content || "";
    }
    const text = el.innerText.replace(/\u200B/g, "").trim();
    const html = el.innerHTML.trim();
    const isEmpty = text === "" || html === "<br>" || html === "";
    el.setAttribute("data-placeholder", "Insert content here...");
    el.classList.toggle("is-empty", isEmpty);
  }, [block.content, isEditing]);

  const paragraphClassName = `block-card paragraph`;

  const viewStyle = {
    textAlign: block.style?.textAlign || "left",
  };

  return isEditing ? (
    <div className="paragraph-block" style={{ position: "relative" }}>
      <PopoverToolbar
        toolbarRef={toolbarRef}
        showPopover={showToolbar}
        onOpenChange={(visible) => {
          if (visible) updateActiveFormats();
        }}
        execCommand={execCommand}
        activeFormats={activeFormats}
        currentAlign={block.style?.textAlign || "left"}
        onAlignChange={handleAlignmentChange}
        onLinkClick={handleLinkClick}
        onFontSizeChange={handleFontSizeChange}
        currentFontSize={currentFontSize}
        onFontColorChange={handleFontColorChange}
        currentFontColor={currentFontColor}
        showRichTextControls={true}
        showAlignControls={true}
      >
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className={paragraphClassName}
          onInput={updateBlockParagraph}
          onMouseUp={handleSelection}
          onKeyUp={handleSelection}
          onFocus={handleSelection}
          tabIndex={0}
          style={{
            outline: "none",
            textAlign: block.style?.textAlign || "left",
          }}
        />
      </PopoverToolbar>
    </div>
  ) : (
    <div
      className={`paragraph-placeholder ${paragraphClassName}`}
      style={viewStyle}
    >
      {block.content?.trim() && block.content.trim() !== "<br>" ? (
        parse(block.content)
      ) : (
        <Skeleton active paragraph={{ rows: 1 }} />
      )}
    </div>
  );
}
